/*
 * Copyright (c) 2025 by PROS, Inc.  All Rights Reserved.
 * This software is the confidential and proprietary information of
 * PROS, Inc. ("Confidential Information").
 * You may not disclose such Confidential Information, and may only
 * use such Confidential Information in accordance with the terms of
 * the license agreement you entered into with PROS.
 */
var PwcAiChatOverlay_1;
import { __decorate } from "tslib";
import { html, css, LitElement } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { SupportedLocale } from '../model.js';
import { prosAiChatUi } from '../pros-ai-chat-ui.js';
import { DefaultSettings } from '../default-settings.js';
/**
 * Overlay event kind.
 */
export const OverlayEventKind = {
    OPEN: 'open',
    CLOSE: 'close',
    MINIMIZE: 'minimize',
    MAXIMIZE: 'maximize',
    RESTORE: 'restore',
};
/**
 * WebComponent.
 */
let PwcAiChatOverlay = PwcAiChatOverlay_1 = class PwcAiChatOverlay extends LitElement {
    constructor() {
        super(...arguments);
        this.locale = SupportedLocale.en_US;
        this._isPanelVisible = false;
        this._isDockedPanelVisible = false;
        this._isMobile = false;
        this._isFullscreen = false;
    }
    /**
     * @inheritdoc
     */
    connectedCallback() {
        // eslint-disable-next-line wc/guard-super-call
        super.connectedCallback();
        // register.
        prosAiChatUi.registerPwcAiChatOverlay(this);
    }
    /**
     * @inheritdoc
     */
    firstUpdated() {
        // init the resize observer.
        this._resizeObserver = new ResizeObserver(entries => {
            this._updateDisplayMode(entries);
        });
        // observe the screen size.
        this._resizeObserver.observe(document.body);
    }
    /**
     * @inheritdoc
     */
    disconnectedCallback() {
        // eslint-disable-next-line wc/guard-super-call
        super.disconnectedCallback();
        // unregister.
        prosAiChatUi.unregisterPwcAiChatOverlay(this);
        // unregister resize observer.
        if (this._resizeObserver) {
            this._resizeObserver.disconnect();
            this._resizeObserver = undefined;
        }
    }
    /**
     * Render the fullscreen background.
     */
    _renderFullscreenBackground() {
        // guard.
        if (!this._isFullscreen) {
            return html ``;
        }
        if (!this._isPanelVisible) {
            return html ``;
        }
        // render the fullscreen background.
        return html ` <div class="fullscreen-background"></div> `;
    }
    /**
     * Render the docked panel.
     */
    _renderPanel() {
        const chatPanelClassList = `${PwcAiChatOverlay_1._getVisibleClassName(this._isPanelVisible)} ${PwcAiChatOverlay_1._getFullscreenClassName(this._isFullscreen)}`;
        // render the panel.
        return html `
      ${this._renderFullscreenBackground()}
      <pwc-ai-chat-panel
        class=${chatPanelClassList}
        .locale=${this.locale}
        .maximized=${this._isFullscreen}
        .disableMaximizeRestore=${this._isMobile}
        @pwc-ai-chat-panel-minimize=${() => {
            this._handleMinimizePanel();
        }}
        @pwc-ai-chat-panel-maximize=${() => {
            this._handleMaximizePanel();
        }}
        @pwc-ai-chat-panel-close=${() => {
            this._handleClosePanel();
        }}
        @pwc-ai-chat-panel-restore=${() => {
            this._handleRestorePanel();
        }}
      >
        <pwc-ai-chat .locale=${this.locale}></pwc-ai-chat>
      </pwc-ai-chat-panel>
    `;
    }
    /**
     * Render the docked panel.
     */
    _renderDockedPanel() {
        // render the docked panel.
        return html `
      <pwc-ai-chat-docked-panel
        class=${PwcAiChatOverlay_1._getVisibleClassName(this._isDockedPanelVisible)}
        .locale=${this.locale}
        .isMobile=${this._isMobile}
        @pwc-ai-chat-docked-panel-restore=${() => {
            this._handleRestoreDockedPanel();
        }}
        @pwc-ai-chat-docked-panel-close=${() => {
            this._handleClosePanel();
        }}
      ></pwc-ai-chat-docked-panel>
    `;
    }
    /**
     * @override
     */
    render() {
        return html ` ${this._renderPanel()} ${this._renderDockedPanel()}`;
    }
    /**
     * Return the visible class name.
     */
    static _getVisibleClassName(isVisible) {
        if (isVisible) {
            return 'is-visible';
        }
        return 'is-hidden';
    }
    /**
     * Return the fullscreen class name.
     */
    static _getFullscreenClassName(isFullscreen) {
        if (isFullscreen) {
            return 'is-fullscreen';
        }
        return 'is-windowed';
    }
    /**
     * When the user minimize the panel.
     */
    _handleMinimizePanel() {
        // update state.
        this._isDockedPanelVisible = true;
        this._isPanelVisible = false;
        // dispatch event.
        this.dispatchEvent(new CustomEvent('pwc-ai-chat-overlay-minimize'));
        // dispatch general purpose event.
        this._emitPwcAiChatOverlayEvent(OverlayEventKind.MINIMIZE);
    }
    /**
     * When the user maximize the panel.
     */
    _handleMaximizePanel() {
        // update the scroll position.
        this._adjustScrollPosition();
        // update state.
        this._isFullscreen = true;
        // dispatch event.
        this.dispatchEvent(new CustomEvent('pwc-ai-chat-overlay-maximize'));
        // dispatch general purpose event.
        this._emitPwcAiChatOverlayEvent(OverlayEventKind.MAXIMIZE);
    }
    /**
     * Adjust the scroll position for a better display.
     */
    _adjustScrollPosition() {
        if (this._pwcAiChatElement) {
            // get current scroll position.
            if (this._pwcAiChatElement) {
                const position = this._pwcAiChatElement.getScrollPosition();
                window.setTimeout(() => {
                    if (this._pwcAiChatElement) {
                        this._pwcAiChatElement.restoreScrollPosition(position);
                    }
                }, 0);
            }
        }
    }
    /**
     * When the user restore the panel.
     */
    _handleRestorePanel() {
        // update the scroll position.
        this._adjustScrollPosition();
        // update state.
        this._isFullscreen = false;
        // dispatch event.
        this.dispatchEvent(new CustomEvent('pwc-ai-chat-overlay-restore'));
        // dispatch general purpose event.
        this._emitPwcAiChatOverlayEvent(OverlayEventKind.RESTORE);
    }
    /**
     * When the user restore the panel.
     */
    _handleRestoreDockedPanel() {
        // update state.
        this._isDockedPanelVisible = false;
        this._isPanelVisible = true;
        // dispatch event.
        this.dispatchEvent(new CustomEvent('pwc-ai-chat-overlay-restore'));
        // dispatch general purpose event.
        this._emitPwcAiChatOverlayEvent(OverlayEventKind.RESTORE);
    }
    /**
     * When the user close the panel.
     */
    _handleClosePanel() {
        // prevent sending an event if the panel is already closed.
        if (!this._isDockedPanelVisible && !this._isPanelVisible) {
            return;
        }
        // update state.
        this._isDockedPanelVisible = false;
        this._isPanelVisible = false;
        // dispatch event.
        this.dispatchEvent(new CustomEvent('pwc-ai-chat-overlay-close'));
        // dispatch general purpose event.
        this._emitPwcAiChatOverlayEvent(OverlayEventKind.CLOSE);
    }
    /**
     * Update the display mode.
     */
    _updateDisplayMode(entries) {
        // guard.
        if (entries.length !== 1) {
            return;
        }
        // get the first entry.
        const entry = entries[0];
        // update the display mode.
        if (entry.contentRect.width <= DefaultSettings.MOBILE_SCREEN_WIDTH_BREAKPOINT) {
            // mode: mobile.
            this._isMobile = true;
            this._isFullscreen = false;
        }
        else {
            // mode: desktop.
            this._isMobile = false;
        }
    }
    /**
     * Emit the `pwc-ai-chat-overlay` event.
     */
    _emitPwcAiChatOverlayEvent(detail) {
        // dispatch general purpose event.
        this.dispatchEvent(new CustomEvent('pwc-ai-chat-overlay', {
            detail,
        }));
    }
    /**
     * Open the panel.
     */
    open() {
        // update state.
        this._isDockedPanelVisible = false;
        this._isPanelVisible = true;
        this._isFullscreen = false;
        // dispatch event.
        this.dispatchEvent(new CustomEvent('pwc-ai-chat-overlay-open'));
        // dispatch general purpose event.
        this._emitPwcAiChatOverlayEvent(OverlayEventKind.OPEN);
    }
    /**
     * Close the panel.
     */
    close() {
        this._handleClosePanel();
    }
};
// styles.
PwcAiChatOverlay.styles = css `
    :host {
      display: inline-block;
      position: relative;

      /* insets */
      --pwc-ai-chat-overlay-docked-panel-inset: var(
        --pwc-inset-ai-chat-panel-docked,
        auto 30px -1px auto
      );
      --pwc-ai-chat-overlay-windowed-panel-inset: var(
        --pwc-inset-ai-chat-panel-windowed,
        50px 30px 50px auto
      );
      --pwc-ai-chat-overlay-windowed-panel-width: var(
        --pwc-width-ai-chat-panel-windowed,
        400px
      );

      --pwc-ai-chat-overlay-fullscreen-panel-inset: var(
        --pwc-inset-ai-chat-panel-fullscreen,
        50px 100px 50px 100px
      );

      /* overlay */
      --pwc-ai-chat-overlay-background-color: var(
        --pwc-overlay-background-color,
        rgb(162 177 185 / 70%)
      );

      /* z-index */
      --pwc-ai-chat-overlay-z-index: var(--pwc-z-index-dialog, 50);
    }

    pwc-ai-chat-panel {
      position: fixed;
      inset: var(--pwc-ai-chat-overlay-windowed-panel-inset);
      width: var(--pwc-ai-chat-overlay-windowed-panel-width);
      z-index: var(--pwc-ai-chat-overlay-z-index);
    }

    pwc-ai-chat-docked-panel {
      position: fixed;
      inset: var(--pwc-ai-chat-overlay-docked-panel-inset);
      z-index: var(--pwc-ai-chat-overlay-z-index);
    }

    pwc-ai-chat-panel.is-fullscreen {
      inset: var(--pwc-ai-chat-overlay-fullscreen-panel-inset);
      width: auto;
      height: auto;
    }

    pwc-ai-chat-panel.is-windowed pwc-ai-chat {
      --pwc-ai-chat-message-width: var(
        --pwc-ai-chat-overlay-windowed-panel-width
      );
    }

    pwc-ai-chat-panel.is-fullscreen pwc-ai-chat {
      --pwc-ai-chat-message-width: 870px;
    }

    @media screen and (max-width: 1280px) {
      pwc-ai-chat-panel.is-fullscreen pwc-ai-chat {
        --pwc-ai-chat-message-width: 700px;
      }
    }

    @media screen and (max-width: 1024px) {
      pwc-ai-chat-panel.is-fullscreen pwc-ai-chat {
        --pwc-ai-chat-message-width: 550px;
      }
    }

    @media screen and (max-width: 800px) {
      pwc-ai-chat-panel.is-fullscreen pwc-ai-chat {
        --pwc-ai-chat-message-width: 400px;
      }
    }

    @media screen and (max-width: 600px) {
      pwc-ai-chat-panel.is-fullscreen pwc-ai-chat,
      pwc-ai-chat-panel.is-windowed pwc-ai-chat {
        --pwc-ai-chat-message-width: 400px;
      }
      pwc-ai-chat-panel.is-fullscreen,
      pwc-ai-chat-panel.is-windowed {
        inset: 0px 0px 0px 0px;
        width: auto;
        height: auto;
        --pwc-border-radius-dialog: 0px;
      }
    }

    @media screen and (max-width: 400px) {
      pwc-ai-chat-panel.is-fullscreen pwc-ai-chat,
      pwc-ai-chat-panel.is-windowed pwc-ai-chat {
        --pwc-ai-chat-message-width: 350px;
      }
      pwc-ai-chat-panel.is-fullscreen,
      pwc-ai-chat-panel.is-windowed {
        inset: 0px 0px 0px 0px;
        width: auto;
        height: auto;
        --pwc-border-radius-dialog: 0px;
      }
    }

    .is-visible {
      visibility: visible;
    }

    .is-hidden {
      visibility: hidden;
    }

    .fullscreen-background {
      position: fixed;
      z-index: var(--pwc-ai-chat-overlay-z-index);
      inset: var(--pwc-inset-modal, 0 0 0 0);
      background: var(--pwc-ai-chat-overlay-background-color);
    }
  `;
__decorate([
    query('pwc-ai-chat', false)
], PwcAiChatOverlay.prototype, "_pwcAiChatElement", void 0);
__decorate([
    property({ attribute: true, type: String })
], PwcAiChatOverlay.prototype, "locale", void 0);
__decorate([
    state()
], PwcAiChatOverlay.prototype, "_isPanelVisible", void 0);
__decorate([
    state()
], PwcAiChatOverlay.prototype, "_isDockedPanelVisible", void 0);
__decorate([
    state()
], PwcAiChatOverlay.prototype, "_isMobile", void 0);
__decorate([
    state()
], PwcAiChatOverlay.prototype, "_isFullscreen", void 0);
PwcAiChatOverlay = PwcAiChatOverlay_1 = __decorate([
    customElement('pwc-ai-chat-overlay')
], PwcAiChatOverlay);
export { PwcAiChatOverlay };
//# sourceMappingURL=pwc-ai-chat-overlay.js.map